import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 基础学科与学术教育 — 神经科学课堂
 *
 * 暗夜实验室 + 发光神经元 + 突触脉冲传递动画
 * 叙事弧：开场提问 → 神经元结构 → 突触传递 → 神经递质类型 → 学习与记忆 → 课堂总结
 * 尺寸：1920 × 1080
 */

interface NeuronPart {
  label: string;
  english: string;
  description: string;
  color: string;
}

interface Neurotransmitter {
  name: string;
  symbol: string;
  effect: string;
  color: string;
}

interface MemoryStage {
  stage: string;
  duration: string;
  description: string;
  icon: string;
}

interface NeuroScienceLectureProps {
  topic: string;
  subtitle: string;
  hookQuestion: string;
  neuronStats: { value: string; label: string }[];
  neuronParts: NeuronPart[];
  synapseExplain: string;
  neurotransmitters: Neurotransmitter[];
  memoryStages: MemoryStage[];
  takeaway: string;
  source: string;
}

const DEFAULT_PROPS: NeuroScienceLectureProps = {
  topic: '大脑是如何思考的？',
  subtitle: 'How Your Brain Actually Thinks · 神经科学第一课',
  hookQuestion: '你正在阅读这行字的瞬间，860亿个神经元正在以每秒120米的速度互相对话',
  neuronStats: [
    { value: '860亿', label: '神经元数量' },
    { value: '100万亿', label: '突触连接' },
    { value: '120 m/s', label: '信号传导速度' },
    { value: '20 W', label: '大脑总能耗' },
  ],
  neuronParts: [
    { label: '树突', english: 'Dendrites', description: '接收上千个神经元的输入信号', color: '#34d399' },
    { label: '胞体', english: 'Soma', description: '汇总信号、决定是否激活', color: '#a78bfa' },
    { label: '轴突', english: 'Axon', description: '将动作电位传递到下一个神经元', color: '#f59e0b' },
    { label: '突触', english: 'Synapse', description: '化学信号的"边境关口"', color: '#f472b6' },
  ],
  synapseExplain:
    '当电信号到达轴突末端，囊泡释放神经递质 → 跨越突触间隙 → 与下一个神经元的受体结合 → 重新转化为电信号。这个过程仅需 0.5 毫秒。',
  neurotransmitters: [
    { name: '多巴胺', symbol: 'DA', effect: '奖赏 · 动机 · 期待', color: '#fbbf24' },
    { name: '血清素', symbol: '5-HT', effect: '情绪 · 食欲 · 睡眠', color: '#34d399' },
    { name: 'γ-氨基丁酸', symbol: 'GABA', effect: '抑制 · 平静 · 焦虑缓解', color: '#60a5fa' },
    { name: '谷氨酸', symbol: 'Glu', effect: '兴奋 · 学习 · 记忆形成', color: '#f87171' },
  ],
  memoryStages: [
    { stage: '感觉记忆', duration: '< 1 秒', description: '视觉/听觉的瞬时缓存', icon: '👁️' },
    { stage: '工作记忆', duration: '15-30 秒', description: '前额叶临时握住信息', icon: '🧮' },
    { stage: '短期记忆', duration: '数分钟-数天', description: '海马体反复巩固', icon: '🔁' },
    { stage: '长期记忆', duration: '终身', description: '突触连接被永久强化（LTP）', icon: '🌳' },
  ],
  takeaway:
    '思考的本质，是 860 亿个神经元之间不断重塑的电化学对话——每一次学习，都在物理上改变你的大脑。',
  source: 'Kandel et al., Principles of Neural Science (6th ed.) · Nature Neuroscience 2024',
};

export const NeuroScienceLectureTemplate: React.FC<
  Partial<NeuroScienceLectureProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const hookStart = 30;
  const statsStart = 80;
  const neuronStart = 150;
  const synapseStart = 240;
  const transmitterStart = 340;
  const memoryStart = 420;
  const takeawayStart = 500;

  const hookOpacity = interpolate(frame, [hookStart, hookStart + 25], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const takeawayOpacity = interpolate(
    frame,
    [takeawayStart, takeawayStart + 30],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const synapsePulse = (frame - synapseStart) / 30;
  const pulseProgress = Math.max(0, Math.min(1, (synapsePulse % 2) / 1));

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 30% 20%, #1e1b4b 0%, #0a0a1f 60%, #050510 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Floating neuron sparks */}
      {Array.from({ length: 60 }).map((_, i) => {
        const x = (i * 311) % 1920;
        const y = (i * 187) % 1080;
        const size = 1.5 + (i % 5) * 0.8;
        const drift = Math.sin(frame * 0.02 + i * 0.7) * 14;
        const driftX = Math.cos(frame * 0.018 + i * 0.5) * 9;
        const flicker = 0.2 + 0.5 * Math.abs(Math.sin(frame * 0.05 + i));
        return (
          <div
            key={`spark-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: i % 3 === 0 ? '#a78bfa' : i % 3 === 1 ? '#34d399' : '#f472b6',
              opacity: flicker,
              boxShadow: `0 0 ${size * 6}px currentColor`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          left: 56,
          right: 56,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 18}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 22,
        }}
      >
        <div
          style={{
            padding: '10px 22px',
            background: 'linear-gradient(135deg, #a78bfa, #f472b6)',
            borderRadius: 10,
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: '0.06em',
          }}
        >
          🧠 神经科学
        </div>
        <h1
          style={{
            fontSize: 52,
            fontWeight: 900,
            margin: 0,
            background: 'linear-gradient(90deg, #ffffff, #e9d5ff, #c4b5fd)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <span style={{ fontSize: 26, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {p.subtitle}
        </span>
      </div>

      {/* ===== Hook question (disabled) ===== */}
      {/*
      <div
        style={{
          position: 'absolute',
          top: 110,
          left: 56,
          right: 56,
          opacity: hookOpacity,
          padding: '16px 28px',
          background:
            'linear-gradient(135deg, rgba(167,139,250,0.12), rgba(244,114,182,0.08))',
          borderLeft: '4px solid #a78bfa',
          borderRadius: '4px 14px 14px 4px',
        }}
      >
        <span style={{ fontSize: 28, color: '#e9d5ff', lineHeight: 1.45 }}>
          {p.hookQuestion}
        </span>
      </div>
      */}

      {/* ===== Big stats strip ===== */}
      <div
        style={{
          position: 'absolute',
          top: 110,
          left: 56,
          right: 56,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.neuronStats.map((s, i) => {
          const sIn = spring({
            frame: frame - (statsStart + i * 10),
            fps,
            config: { damping: 14, stiffness: 110 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                padding: '14px 18px',
                background: 'rgba(15,15,35,0.7)',
                border: '1px solid rgba(167,139,250,0.25)',
                borderRadius: 14,
                opacity: sIn,
                transform: `translateY(${(1 - sIn) * 24}px)`,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #c4b5fd, #f472b6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                }}
              >
                {s.value}
              </div>
              <div
                style={{
                  fontSize: 20,
                  color: '#94a3b8',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Neuron diagram (SVG, left half) ===== */}
      <div style={{ position: 'absolute', top: 200, left: 56, width: 880 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            letterSpacing: '0.08em',
            marginBottom: 10,
            fontWeight: 700,
          }}
        >
          ▣ 神经元解剖
        </div>

        <svg width="880" height="380" viewBox="0 0 880 380">
          <defs>
            <radialGradient id="somaGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#c4b5fd" />
              <stop offset="60%" stopColor="#7c3aed" />
              <stop offset="100%" stopColor="#3b0764" />
            </radialGradient>
            <filter id="softGlow">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Diagram body (shifted upward to leave room for labels) */}
          <g transform="translate(0, -20)">
          {/* Dendrites — branching from soma */}
          {(() => {
            const dendrites: { x: number; y: number }[] = [
              { x: 40, y: 60 }, { x: 30, y: 130 }, { x: 25, y: 200 },
              { x: 35, y: 280 }, { x: 60, y: 330 }, { x: 110, y: 50 },
              { x: 130, y: 320 },
            ];
            const dendriteIn = interpolate(
              frame,
              [neuronStart, neuronStart + 30],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return dendrites.map((d, i) => (
              <g key={`dend-${i}`} opacity={dendriteIn}>
                <path
                  d={`M${d.x},${d.y} Q ${(d.x + 220) / 2},${(d.y + 190) / 2 - 20} 220,190`}
                  stroke="#34d399"
                  strokeWidth="2.5"
                  fill="none"
                  opacity="0.7"
                />
                <circle cx={d.x} cy={d.y} r="6" fill="#34d399" filter="url(#softGlow)" />
              </g>
            ));
          })()}

          {/* Soma (cell body) */}
          <circle
            cx="220"
            cy="190"
            r={50 + Math.sin(frame * 0.06) * 3}
            fill="url(#somaGrad)"
            filter="url(#softGlow)"
            opacity={interpolate(
              frame,
              [neuronStart + 20, neuronStart + 50],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          />
          <circle cx="220" cy="190" r="20" fill="#1e1b4b" opacity="0.7" />
          <text x="220" y="196" textAnchor="middle" fill="#e9d5ff" fontSize="14" fontWeight="700">
            核
          </text>

          {/* Axon — long line */}
          <line
            x1="270"
            y1="190"
            x2="700"
            y2="190"
            stroke="#f59e0b"
            strokeWidth="6"
            strokeLinecap="round"
            opacity={interpolate(
              frame,
              [neuronStart + 40, neuronStart + 70],
              [0, 0.85],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          />
          {/* Myelin sheaths */}
          {[320, 400, 480, 560, 640].map((x, i) => (
            <ellipse
              key={`myelin-${i}`}
              cx={x}
              cy={190}
              rx={26}
              ry={14}
              fill="rgba(245,158,11,0.18)"
              stroke="#f59e0b"
              strokeWidth="1.5"
              opacity={interpolate(
                frame,
                [neuronStart + 50 + i * 6, neuronStart + 70 + i * 6],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />
          ))}

          {/* Action potential pulse running along axon */}
          {(() => {
            const localStart = synapseStart;
            const localFrame = frame - localStart;
            const t = (localFrame % 60) / 60;
            const pulseX = 270 + t * 430;
            const visible = localFrame > 0 && t < 0.95;
            return visible ? (
              <g>
                <circle cx={pulseX} cy="190" r="14" fill="#fde047" filter="url(#softGlow)" />
                <circle cx={pulseX} cy="190" r="22" fill="none" stroke="#fef3c7" strokeWidth="2" opacity="0.5" />
              </g>
            ) : null;
          })()}

          {/* Axon terminal (target) */}
          <g
            opacity={interpolate(
              frame,
              [neuronStart + 60, neuronStart + 90],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <circle cx="710" cy="160" r="10" fill="#f472b6" filter="url(#softGlow)" />
            <circle cx="720" cy="190" r="12" fill="#f472b6" filter="url(#softGlow)" />
            <circle cx="710" cy="220" r="9" fill="#f472b6" filter="url(#softGlow)" />
          </g>

          {/* Receiving neuron (next) */}
          <g
            opacity={interpolate(
              frame,
              [neuronStart + 70, neuronStart + 100],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <circle cx="800" cy="190" r="36" fill="rgba(124,58,237,0.4)" filter="url(#softGlow)" />
            <line x1="836" y1="190" x2="870" y2="170" stroke="#34d399" strokeWidth="2" opacity="0.6" />
            <line x1="836" y1="190" x2="870" y2="190" stroke="#34d399" strokeWidth="2" opacity="0.6" />
            <line x1="836" y1="190" x2="870" y2="210" stroke="#34d399" strokeWidth="2" opacity="0.6" />
          </g>
          </g>

          {/* Labels */}
          {p.neuronParts.map((part, i) => {
            const positions = [
              { x: 30, y: 368 },
              { x: 200, y: 368 },
              { x: 470, y: 368 },
              { x: 720, y: 368 },
            ];
            const lblIn = spring({
              frame: frame - (neuronStart + 80 + i * 8),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <g
                key={`lbl-${i}`}
                opacity={lblIn}
                transform={`translate(${positions[i].x}, ${positions[i].y - (1 - lblIn) * 8})`}
              >
                <rect
                  x="0"
                  y="-26"
                  width="110"
                  height="26"
                  rx="6"
                  fill="rgba(15,15,35,0.85)"
                  stroke={part.color}
                  strokeWidth="1.5"
                />
                <text x="12" y="-7" fill={part.color} fontSize="18" fontWeight="800">
                  {part.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Part descriptions list */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
          {p.neuronParts.map((part, i) => {
            const sIn = spring({
              frame: frame - (neuronStart + 100 + i * 8),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={`desc-${i}`}
                style={{
                  width: 'calc(50% - 5px)',
                  padding: '10px 14px',
                  background: 'rgba(15,15,35,0.6)',
                  borderLeft: `3px solid ${part.color}`,
                  borderRadius: 8,
                  opacity: sIn,
                  transform: `translateX(${(1 - sIn) * 12}px)`,
                  fontSize: 20,
                  color: '#cbd5e1',
                  lineHeight: 1.4,
                }}
              >
                <span style={{ color: part.color, fontWeight: 700 }}>{part.label} · </span>
                {part.description}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Synapse close-up (right column) ===== */}
      <div style={{ position: 'absolute', top: 210, left: 970, width: 440 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            letterSpacing: '0.08em',
            marginBottom: 10,
            fontWeight: 700,
          }}
        >
          ▣ 突触传递
        </div>
        <div
          style={{
            position: 'relative',
            height: 220,
            background: 'rgba(15,15,35,0.7)',
            border: '1px solid rgba(244,114,182,0.25)',
            borderRadius: 14,
            overflow: 'hidden',
          }}
        >
          {/* Pre-synaptic terminal */}
          <div
            style={{
              position: 'absolute',
              left: 30,
              top: 50,
              width: 140,
              height: 120,
              borderRadius: '50% 30% 50% 30%',
              background: 'radial-gradient(circle at 60% 40%, #f472b6, #831843 90%)',
              boxShadow: '0 0 30px rgba(244,114,182,0.4)',
            }}
          />
          {/* Vesicles */}
          {[
            { x: 60, y: 90 }, { x: 90, y: 110 }, { x: 110, y: 80 },
            { x: 80, y: 130 }, { x: 130, y: 120 },
          ].map((v, i) => (
            <div
              key={`ves-${i}`}
              style={{
                position: 'absolute',
                left: v.x,
                top: v.y,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: '#fbbf24',
                boxShadow: '0 0 8px rgba(251,191,36,0.6)',
                opacity: 0.85 - pulseProgress * 0.5,
              }}
            />
          ))}
          {/* Synaptic gap */}
          <div
            style={{
              position: 'absolute',
              left: 175,
              top: 60,
              width: 60,
              height: 100,
              border: '1px dashed rgba(255,255,255,0.15)',
              borderRadius: 4,
            }}
          />
          {/* Released neurotransmitters traversing the cleft */}
          {[0, 1, 2, 3].map((i) => {
            const offsetT = (pulseProgress + i * 0.2) % 1;
            const x = 175 + offsetT * 60;
            const y = 80 + (i % 2 === 0 ? 0 : 20);
            return (
              <div
                key={`nt-${i}`}
                style={{
                  position: 'absolute',
                  left: x,
                  top: y,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#fbbf24',
                  boxShadow: '0 0 10px #fbbf24',
                  opacity: 0.7,
                }}
              />
            );
          })}
          {/* Post-synaptic membrane */}
          <div
            style={{
              position: 'absolute',
              right: 30,
              top: 50,
              width: 140,
              height: 140,
              borderRadius: '30% 50% 30% 50%',
              background: 'radial-gradient(circle at 30% 50%, #7c3aed, #1e1b4b 90%)',
              boxShadow: '0 0 30px rgba(124,58,237,0.4)',
            }}
          />
          {/* Receptors */}
          {[60, 90, 120, 150].map((y, i) => (
            <div
              key={`rcp-${i}`}
              style={{
                position: 'absolute',
                right: 165,
                top: y,
                width: 12,
                height: 6,
                borderRadius: 2,
                background: '#34d399',
                boxShadow: '0 0 8px rgba(52,211,153,0.6)',
              }}
            />
          ))}
          {/* Labels */}
          <div
            style={{
              position: 'absolute',
              left: 50,
              top: 8,
              fontSize: 20,
              color: '#f472b6',
              fontWeight: 700,
            }}
          >
            突触前
          </div>
          <div
            style={{
              position: 'absolute',
              right: 60,
              top: 8,
              fontSize: 20,
              color: '#a78bfa',
              fontWeight: 700,
            }}
          >
            突触后
          </div>
          <div
            style={{
              position: 'absolute',
              left: 178,
              bottom: 6,
              fontSize: 20,
              color: '#94a3b8',
            }}
          >
            突触间隙
          </div>
        </div>
        <div
          style={{
            fontSize: 20,
            color: '#cbd5e1',
            lineHeight: 1.55,
            marginTop: 14,
            padding: '12px 16px',
            background: 'rgba(15,15,35,0.6)',
            borderRadius: 10,
          }}
        >
          {p.synapseExplain}
        </div>
      </div>

      {/* ===== Neurotransmitters (right far) ===== */}
      <div style={{ position: 'absolute', top: 210, left: 1440, width: 440 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            letterSpacing: '0.08em',
            marginBottom: 10,
            fontWeight: 700,
          }}
        >
          ▣ 主要神经递质
        </div>
        {p.neurotransmitters.map((nt, i) => {
          const sIn = spring({
            frame: frame - (transmitterStart + i * 10),
            fps,
            config: { damping: 14, stiffness: 110 },
          });
          return (
            <div
              key={`nt-row-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 16px',
                marginBottom: 10,
                background: 'rgba(15,15,35,0.7)',
                borderRadius: 12,
                border: `1px solid ${nt.color}30`,
                opacity: sIn,
                transform: `translateX(${(1 - sIn) * 24}px)`,
              }}
            >
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${nt.color}, ${nt.color}30)`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: `0 0 16px ${nt.color}55`,
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0a0a1f' }}>
                  {nt.symbol}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: nt.color }}>
                  {nt.name}
                </div>
                <div style={{ fontSize: 20, color: '#cbd5e1', marginTop: 4 }}>
                  {nt.effect}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Memory pipeline (bottom strip) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 768,
          left: 56,
          right: 56,
          padding: '12px 20px 14px',
          background:
            'linear-gradient(135deg, rgba(167,139,250,0.08), rgba(52,211,153,0.05))',
          border: '1px solid rgba(167,139,250,0.2)',
          borderRadius: 14,
        }}
      >
        <div
          style={{
            fontSize: 24,
            color: '#94a3b8',
            letterSpacing: '0.1em',
            marginBottom: 10,
            fontWeight: 700,
          }}
        >
          ▣ 记忆的四级流水线
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
          {p.memoryStages.map((m, i) => {
            const sIn = spring({
              frame: frame - (memoryStart + i * 12),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <React.Fragment key={`mem-${i}`}>
                <div
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    background: 'rgba(10,10,31,0.55)',
                    border: '1px solid rgba(167,139,250,0.25)',
                    borderRadius: 12,
                    opacity: sIn,
                    transform: `translateY(${(1 - sIn) * 16}px) scale(${0.9 + 0.1 * sIn})`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 2,
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{m.icon}</span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#e9d5ff' }}>
                      {m.stage}
                    </span>
                  </div>
                  <div style={{ fontSize: 22, color: '#fbbf24', fontWeight: 700 }}>
                    {m.duration}
                  </div>
                  <div style={{ fontSize: 22, color: '#cbd5e1', marginTop: 2, lineHeight: 1.35 }}>
                    {m.description}
                  </div>
                </div>
                {i < p.memoryStages.length - 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#a78bfa',
                      fontSize: 28,
                      opacity: sIn,
                    }}
                  >
                    →
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== Takeaway + source ===== */}
      <div
        style={{
          position: 'absolute',
          top: 968,
          left: 56,
          right: 56,
          padding: '12px 24px',
          background:
            'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(244,114,182,0.12))',
          borderRadius: 12,
          border: '1.5px solid rgba(167,139,250,0.3)',
          opacity: takeawayOpacity,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <span style={{ fontSize: 32 }}>💡</span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: '#fff',
              lineHeight: 1.4,
              textShadow: '0 0 10px rgba(167,139,250,0.4)',
            }}
          >
            {p.takeaway}
          </div>
          <div style={{ fontSize: 24, color: '#94a3b8', marginTop: 6 }}>
            {p.source}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 600], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #34d399, #a78bfa, #f472b6, #fbbf24)',
        }}
      />
    </AbsoluteFill>
  );
};
